/**
 * LightColorButton - set a stage colour on a target. The hero of the
 * lighting set: "I want the back wash deep blue" is one tap.
 *
 * ## The face carries the colour, and that is the whole design
 *
 * Every other button in this app tells you what it does in words. This one
 * can SHOW it, because the thing it does is a colour - so the tile is painted
 * in the swatch it sends. That is the ethos bullet "show state without
 * requiring a click", and it is why a row of eight of these reads at a glance
 * on a dark stage while eight identically-styled buttons labelled "Red",
 * "Amber", "Cyan" would not.
 *
 * Label colour is chosen by LUMINANCE, not by hue - a flat RGB average calls
 * pure green mid-grey and puts white text on the brightest swatch in the
 * palette. Same lesson as the state-must-be-luminance convention, applied to
 * legibility rather than to state.
 *
 * ## Dead state is loud, and names WHICH of three things is wrong
 *
 * A control pointing at nothing must say so (DEC-124/138). Lighting has three
 * distinct ways to be dead and they have three different fixes, so they are
 * not collapsed: an empty rig, a group whose fixtures are all gone, and a
 * deleted fixture. `targetDeadReason` owns that discrimination - the same
 * refusal to collapse distinct failures that `bridgeSync`'s multi-valued
 * status exists for.
 *
 * ## What the toast may claim
 *
 * A group write is N requests, so partial success is real and is reported as
 * "4/5". The toast says OPBridge ACCEPTED the write - never that a light
 * changed. Phase 4 (`/dmx/fixtures/resolved`) is what will license the
 * stronger sentence; until then this stays inside what it actually knows,
 * the same boundary DEC-124's send-success lighting draws for MIDI.
 */

import { createSignal } from 'solid-js';
import { editOwnsGesture, selectedItemId, type LightColorItem, type LayoutColor } from '../layout/store.ts';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { pushToast } from '../ui/toast.ts';
import { sendDmxState, dmxReachableReactive } from '../opbridge/dispatch.ts';
import { resolveTarget, targetSubfixture, describeTarget, targetDeadReason } from '../lighting/targets.ts';
import { stageColorByLabel, rgb255, rgbToHex, onColorText } from '../lighting/palette.ts';
import { watchLighting, heldByOther, isParked } from '../lighting/liveState.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

interface LightColorButtonProps {
  item: LightColorItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function LightColorButton(props: LightColorButtonProps) {
  // Phase 4: opt this control's page into the live-state poll for as long as
  // it is mounted. Reference-counted, so a page with no lighting on it never
  // makes the request.
  watchLighting();
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [firing, setFiring] = createSignal(false);

  const swatch = () => stageColorByLabel(props.item.color_label);
  const deadReason = () => (swatch() ? targetDeadReason(props.item.target) : 'colour removed');
  // The default, or the computers that output these lights (audit A-9).
  const live = () => !deadReason() && dmxReachableReactive(resolveTarget(props.item.target).map((f) => f.id));

  async function fire(): Promise<void> {
    const sw = swatch();
    if (!sw) return;
    const targets = resolveTarget(props.item.target);
    if (targets.length === 0) return;
    setFiring(true);
    try {
      const sub = targetSubfixture(props.item.target);
      const results = await sendDmxState(
        targets.map((f) => f.id),
        {
          rgb: rgb255(sw.rgb),
          // POWER. Not optional, and the omission was a real bug caught by
          // `light_controls_move_the_rig` on its first run: OPBridge's
          // `on:false` means "black the wire but REMEMBER the colour", every
          // fixture on this rig rests at `on:false`, and `/state` leaves an
          // omitted field alone. So sending a colour WITHOUT `on` stored the
          // colour as intent, returned 200, showed a success toast, and left
          // the light dark. Measured: `{rgb:(0,0,255)}` -> readback (0,0,0);
          // a bare `{on:true}` afterwards -> readback (0,0,255).
          //
          // The "Off" swatch is the deliberate exception. It is the
          // operator's "turn this off", so it powers DOWN - which also means
          // the fixture remembers its colour and the next colour button
          // restores it, rather than every light coming back white.
          on: props.item.color_label !== 'Off',
          // Level rides along ONLY when the author set one. Omitted means
          // "leave the level alone" on OPBridge's partial-write route, which
          // is why a colour button cannot silently full-brighten a rig the
          // operator had deliberately dimmed.
          ...(props.item.level !== undefined ? { level: props.item.level } : {}),
          ...(sub !== undefined ? { subfixture: sub } : {}),
        },
      );
      const ok = results.filter((r) => r.kind === 'ok').length;
      const total = results.length;
      if (ok === total) {
        pushToast('ok', `${props.item.color_label} - ${describeTarget(props.item.target)}`, 1500);
      } else {
        const why = results.find((r) => r.kind !== 'ok');
        pushToast(
          'error',
          `${props.item.color_label} reached ${ok}/${total}${why?.detail ? ` - ${why.detail}` : ''}`,
          3500,
        );
      }
    } finally {
      setFiring(false);
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return; // right-click escape hatch
    if (!live()) return;
    e.stopPropagation();
    if (!guard.gate('fire')) return; // guarded: first tap only arms
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel || !!deadReason();
  const faceText = () => props.item.name ?? props.item.color_label;
  /**
   * Who else is driving the lights this control aims at.
   *
   * This is the Phase 4 payoff and it answers a question the operator would
   * otherwise ask out loud mid-set: "why did nothing happen?" Every fixture
   * on this rig rests `held by playback`, so a tap against a running timeline
   * genuinely can be overridden - and a button that silently loses is
   * indistinguishable from a broken one.
   *
   * PARKED is called out separately and ranks above a plain hold, because it
   * is a stronger statement: a parked fixture (DEC-482) ignores playback
   * entirely until an explicit release, so "parked" tells the operator their
   * timeline will not move this light either.
   *
   * Reports only when EVERY targeted fixture agrees. A group where one light
   * is held and three are free is a control that mostly works, and
   * "held by playback" on it would be a lie about the other three.
   */
  const liveNote = () => {
    const targets = resolveTarget(props.item.target);
    if (targets.length === 0) return undefined;
    if (targets.every((f) => isParked(f.id))) return 'PARKED';
    const holders = targets.map((f) => heldByOther(f.id));
    const first = holders[0];
    // Bare owner name, not "held by X" - the combined label below reads
    // "wash (2) · playback", and the full phrase does not fit beside a group
    // name at 10px in a 130px tile.
    if (first && holders.every((h) => h === first)) return first;
    return undefined;
  };

  const subText = () => {
    const dead = deadReason();
    if (dead) return dead;
    if (!live()) return 'no bridge';
    if (firing()) return 'sending...';
    // Target FIRST, condition behind it. The reverse was tried and the
    // screenshot killed it: every button on this rig read "held by playback"
    // and none said which lights it aimed at.
    const note = liveNote();
    const where = describeTarget(props.item.target);
    return note ? `${where} · ${note}` : where;
  };

  // The swatch paints the face. `--light-swatch` / `--light-swatch-ink` are
  // consumed by `.midi-button[data-light-swatch]` in app.css; setting them as
  // inline custom properties rather than a direct background keeps the skin's
  // bevel, press and armed treatments intact - a flat inline `background`
  // would paint over every one of them and the button would stop looking like
  // the rest of the rig. The "Off" swatch is pure black and would read as a
  // hole, so it renders as a dashed outline instead, the same convention the
  // Timeline palette already uses for it.
  const sw = () => swatch();
  const isOff = () => props.item.color_label === 'Off';

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      data-on="false"
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      data-light-swatch={sw() && !isOff() ? 'true' : undefined}
      data-light-off={isOff() ? 'true' : undefined}
      style={sw() && !isOff()
        ? {
            '--light-swatch': rgbToHex(sw()!.rgb),
            '--light-swatch-ink': onColorText(sw()!.rgb),
          }
        : undefined}
      onPointerDown={onPointerDown}
      disabled={!live()}
    >
      <PadDecor item={props.item} />
      {showName() && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {showLabel() && <span class="midi-button-label">{subText()}</span>}
    </button>
  );
}
